import { forwardRef } from "react";
import {
  calcOfferTotals,
  mergeOfferWritingForExport,
} from "@/lib/offer-calc";
import { buildLeftPanelBlocks } from "@/lib/offer-export";
import { OFFER_EXPORT_FONT_BODY_CSS, OFFER_EXPORT_HEX } from "@/lib/offer-export-brand";
import { exportPhaseLabel } from "@/lib/offer-export-table";
import type { BudgetPageSlice } from "@/lib/offer-export-paginate";
import type { OfferSection } from "@/hooks/use-offer-sections";
import {
  PHASE_ORDER,
  isLineVisible,
  splitPhasesToColumns,
  type PhaseGroup,
} from "@/lib/offer-budget-image-geometry";
import { PhaseTable } from "./offer-budget-image/OfferBudgetImageOverlay";
import {
  BudgetTotalsTable,
  SectionTilvalgBlocks,
} from "./offer-budget-image/OfferBudgetImageToolbar";
import {
  OfferBudgetImageLeft,
  OfferExportFoxBadge,
} from "./offer-budget-image/OfferBudgetImageLeft";

import type { Offer, OfferLine } from "@/hooks/use-offers";

interface Props {
  offer: Offer;
  lines: OfferLine[];
  fontSize?: number;
  sections?: OfferSection[];
  exportSectionKey?: string | null;
  twoColumns?: boolean;
  /** Når satt vises kun denne siden (flerside-PNG for store budsjett). */
  pageSlice?: BudgetPageSlice | null;
}

/**
 * Liggende 16:9-budsjettbilde 1:1 mot Sølvrev-tilbudsmalen: rosa asterisk,
 * kompakt tabell i Revegrå, rev i Reveblå-sirkel. Store budsjett pagineres
 * via `pageSlice` i lesbar størrelse i stedet for å krympes til 7px.
 */
export const OfferBudgetImage = forwardRef<HTMLDivElement, Props>(
  function OfferBudgetImage(
    {
      offer,
      lines,
      sections = [],
      fontSize = 13,
      exportSectionKey,
      twoColumns = false,
      pageSlice = null,
    },
    ref,
  ) {
    const exportLines = mergeOfferWritingForExport(lines);
    const visibleLines = exportLines.filter(isLineVisible);

    const isSingleOptionExport = typeof exportSectionKey === "string";
    const selectedSection = isSingleOptionExport
      ? sections.find((s) => s.id === exportSectionKey) ?? null
      : null;
    const showTilvalgBlocks =
      exportSectionKey === undefined && !pageSlice;

    const primaryLines = isSingleOptionExport
      ? lines.filter((l) => l.section_id === exportSectionKey)
      : lines.filter((l) => !l.section_id);

    const sectionGroups = showTilvalgBlocks
      ? sections
          .slice()
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((s) => ({
            section: s,
            lines: visibleLines.filter((l) => l.section_id === s.id),
            subtotal: visibleLines
              .filter((l) => l.section_id === s.id)
              .reduce((sum, l) => sum + (l.amount ?? 0), 0),
          }))
          .filter((g) => g.lines.length > 0)
      : [];

    const totals = calcOfferTotals(
      {
        overhead_pct: offer.overhead_pct,
        discount_pct: offer.discount_pct,
        discount_amount: isSingleOptionExport ? 0 : offer.discount_amount,
        include_travel_in_overhead: offer.include_travel_in_overhead,
      },
      primaryLines.map((l) => ({
        phase: l.phase,
        cost_type: l.cost_type,
        pricing_type: l.pricing_type,
        quantity: l.quantity,
        unit: l.unit,
        unit_price: l.unit_price,
        purchase_price: l.purchase_price,
        amount: l.amount,
        overtime_split: l.overtime_split ?? null,
      })),
    );

    const mainVisibleLines = visibleLines.filter((l) =>
      isSingleOptionExport ? l.section_id === exportSectionKey : !l.section_id,
    );

    const tableLines = pageSlice?.lines ?? mainVisibleLines;
    const showTotals = pageSlice ? pageSlice.showTotals : true;

    const phaseGroups: PhaseGroup[] = PHASE_ORDER.map(({ key }) => ({
      key,
      label: exportPhaseLabel(key),
      lines: tableLines.filter((l) => l.phase === key),
    })).filter((p) => p.lines.length > 0);

    const [col1Phases, col2Phases] = twoColumns
      ? splitPhasesToColumns(phaseGroups)
      : [phaseGroups, [] as PhaseGroup[]];

    // Hovedbudsjettet har ingen vannrett overskrift — den roterte
    // «budsjett_» i venstrepanelet sier allerede hva sliden er (Erik 29.09).
    const exportTitle = isSingleOptionExport
      ? `Tilvalg: ${selectedSection?.name ?? "Tilvalg"}`
      : null;

    const discountPctText =
      offer.discount_amount > 0
        ? ""
        : offer.discount_pct > 0
          ? `-${offer.discount_pct} %`
          : "";

    const customTermsText = isSingleOptionExport
      ? (selectedSection?.custom_terms_text ?? "").trim()
      : (offer.custom_terms_text ?? "").trim();

    const leftBlocks = buildLeftPanelBlocks(offer, customTermsText);
    const pageLabel =
      pageSlice && pageSlice.pageTotal > 1
        ? `Side ${pageSlice.pageNumber} av ${pageSlice.pageTotal}`
        : null;

    const compactTable = !twoColumns;

    return (
      <div
        ref={ref}
        style={{
          width: 1280,
          height: 720,
          display: "flex",
          background: OFFER_EXPORT_HEX.dark,
          fontFamily: OFFER_EXPORT_FONT_BODY_CSS,
          color: OFFER_EXPORT_HEX.darkText,
          overflow: "hidden",
        }}
      >
        <OfferBudgetImageLeft
          title={exportTitle}
          subtitle={offer.title ?? ""}
          blocks={leftBlocks}
          pageLabel={pageLabel}
        />

        <div
          style={{
            flex: 1,
            background: OFFER_EXPORT_HEX.tableBg,
            position: "relative",
            minWidth: 0,
          }}
        >
          <div
            data-budget-panel="root"
            style={{
              width: "100%",
              height: "100%",
              // VIB-2556: malens tabell starter 1,445" inn i høyrepanelet
              // og har 72 px topp- og bunnmarg (1,51" / 1,49" av 15").
              padding: compactTable ? "72px 124px 72px 139px" : "36px 36px 72px 36px",
              color: OFFER_EXPORT_HEX.tableText,
              fontFamily: OFFER_EXPORT_FONT_BODY_CSS,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                // Malens tabellbredde 3,87" → 371 px på 96 px/tomme.
                width: compactTable ? 371 : "100%",
                maxWidth: "100%",
              }}
            >
              <div
                data-budget-content="lines"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: twoColumns ? 16 : 0,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <PhaseTable phases={col1Phases} fontSize={fontSize} />
                </div>
                {twoColumns && (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <PhaseTable phases={col2Phases} fontSize={fontSize} />
                  </div>
                )}
              </div>

              {showTotals && (
                <BudgetTotalsTable
                  fontSize={fontSize}
                  totals={totals}
                  overheadPct={offer.overhead_pct}
                  discountPctText={discountPctText}
                />
              )}

              <SectionTilvalgBlocks
                sectionGroups={sectionGroups}
                fontSize={fontSize}
              />
            </div>
          </div>

          <div style={{ position: "absolute", right: 36, bottom: 32 }}>
            <OfferExportFoxBadge size={48} />
          </div>
        </div>
      </div>
    );
  },
);
